import { Box, LinearProgress, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import _ from 'lodash'
import { ROWS_PER_PAGE_OPTIONS } from 'constants'
import { capitalize } from 'lodash';
import ActionMenu from "./ActionMenu";
import { useNavigate } from "react-router-dom";
import { BuildCircleRounded } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useSession } from 'components/Providers';


export default function AssignAssetList() {
  const { t } = useTranslation()
  const basePath = '/asset-management/my-devices'
  const navigate = useNavigate()
  const { district } = useSession()
  const assetLoading = useSelector(state => state.asset.assetManagement.assetLoading)
  const query = useSelector(state => state.asset.assetManagement.query)
  const types = useSelector(state => state.lookup.types)
  const assetCondition = types?.filter(type => type.category === 'assetConditionType') || []
  const assetStatus = types?.filter(type => type.category === 'assetStatusType') || []
  const assetType = types?.filter(type => type.category === 'assetType') || []
  const assetList = useSelector(state => state.asset.assetManagement.list)
  const showCampus = district?.isServiceProvider && !query?.campusId


  const handleUpdateClick = (row) => {
    navigate(`${basePath}/${row.id}`, { state: { action: 'edit' } });
  };

  const handleAssignClick = (row, actionType) => {
    navigate(`${basePath}/${row.id}/assign`, { state: { action: actionType } });
  };

  const handleAssetLogs = (row) => {
    navigate(`${basePath}/${row.id}/logs`, { state: { action: 'assetLogs' } });
  };

  const handleTransferClick = (row) => {
    navigate(`${basePath}/${row.id}/transfer`, { state: { action: 'transfer' } });
  };


  const campusColumn = {
    field: 'campusId',
    headerName: t('Campus'),
    editable: false,
    sortable: true,
    flex: 1,
    renderCell: (params) => params?.row?.campus?.name || ' ',
  }

  const tableColumns = [
    ...(showCampus ? [campusColumn] : []),
    {
      field: 'brand',
      headerName: t('Brand'),
      editable: false,
      sortable: true,
      flex: 1,
      renderCell: (params) => capitalize(params.value || ' ')
    },
    {
      field: 'typeId',
      headerName: t('Type'),
      editable: false,
      sortable: true,
      flex: 1,
      renderCell: (params) => assetType.find(type => type.code === params.value)?.name || ' '
    },
    {
      field: 'assetNumber',
      headerName: t('Asset Number'),
      editable: false,
      sortable: true,
      flex: 1,
    },
    {
      field: 'serialNumber',
      headerName: t('Serial Number'),
      editable: false,
      sortable: true,
      flex: 1,
    },
    {
      field: 'conditionId',
      headerName: t('Condition'),
      editable: false,
      sortable: true,
      flex: 1,
      renderCell: (params) => assetCondition.find(type => type.code === params.value)?.name || ' '
    },
    {
      field: 'statusId',
      headerName: t('Status'),
      editable: false,
      sortable: true,
      flex: 1,
      renderCell: (params) => assetStatus.find(type => type.code === params.value)?.name || ' '
    },
    {
      field: 'assignDate',
      headerName: t('Assign Date'),
      editable: false,
      sortable: true,
      flex: 1,
      renderCell: (params) => params.row.assignInfo?.assignedDate || '',
    },
    {
      field: 'assignee',
      headerName: t('Assignee'),
      editable: false,
      sortable: true,
      flex: 1,
      renderCell: (params) => {
        const assignInfo = params.row.assignInfo;
        const type = assignInfo.type;
        const name = assignInfo.name;

        if (!assignInfo || type == null || name == null) return '';

        return `[${type.toUpperCase()}] ${name}`;
      }
    },
    {
      field: 'action',
      headerName: t('Action'),
      editable: false,
      sortable: false,
      flex: 1,
      disableColumnMenu: true,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        const status = row.statusId;
        const inRepair = row.inRepair;

        let canAssignOrLoan = ['unassigned'].includes(status);
        let canReturn = ['assigned', 'loaned'].includes(status);
        let canRepair = ['unassigned', 'assigned', 'loaned'].includes(status);
        let canMarkLost = ['unassigned', 'assigned', 'loaned'].includes(status);
        let canDeprovision = ['unassigned', 'assigned'].includes(status);
        let canRestock = ['lost', 'deprovisioned'].includes(status);
        let canMarkRepaired = ['unassigned', 'assigned', 'loaned'].includes(status) && inRepair;

        if (inRepair === true && status === 'unassigned') {
          canAssignOrLoan = true;
          canRepair = false;
        } else if (inRepair === true) {
          canAssignOrLoan = false;
          canReturn = false;
          canRepair = false;
        }

        const options = [
          {
            label: 'Assign',
            visible: canAssignOrLoan,
            action: () => handleAssignClick(row, 'assign'),
          },
          {
            label: 'Loan',
            visible: canAssignOrLoan,
            action: () => handleAssignClick(row, 'loan'),
          },
          {
            label: 'Return',
            visible: canReturn,
            action: () => handleAssignClick(row, 'return'),
          },
          {
            label: 'Repair',
            visible: canRepair,
            action: () => handleAssignClick(row, 'repair'),
          },
          {
            label: 'Lost',
            visible: canMarkLost,
            action: () => handleAssignClick(row, 'lost'),
          },
          {
            label: 'Deprovisioned',
            visible: canDeprovision,
            action: () => handleAssignClick(row, 'deprovisioned'),
          },
          {
            label: 'Restock',
            visible: canRestock,
            action: () => handleAssignClick(row, 'restock'),
          },
          {
            label: 'Repaired',
            visible: canMarkRepaired,
            action: () => handleAssignClick(row, 'repaired'),
          },
          {
            label: 'Update Asset',
            visible: true,
            action: () => handleUpdateClick(row),
          },
          {
            label: 'Transfer Asset',
            visible: ['assigned', 'unassigned'].includes(status),
            action: () => handleTransferClick(row),
          },
          {
            label: 'Asset Log',
            visible: true,
            action: () => handleAssetLogs(row),
          },
        ];

        return (
          <Box display="flex" alignItems="center">
            <ActionMenu options={options.filter(opt => opt.visible)} />
            {inRepair && (
              <Tooltip title="This asset is in repair" arrow>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: '8px',
                    color: 'red'
                  }}
                >
                  <BuildCircleRounded />
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      }
    }

  ]

  const pageSizeOptions = _.uniq([...ROWS_PER_PAGE_OPTIONS])
  if (assetLoading) return <LinearProgress />

  return (
    <Box mb={2} width={'100%'}>
      <Box p={1}>
        <DataGrid
          columns={tableColumns}
          rows={assetList}
          pageSizeOptions={pageSizeOptions}
          disableRowSelectionOnClick
          loading={assetLoading}
        />
      </Box>
    </Box>
  )
}